/**
 * قيّم أرض — تتابع أسئلة، سؤال واحد في كل خطوة.
 *
 * ترتيب الأسئلة بأثر غياب الإجابة على المخرَج، لا بعادة النماذج:
 *   ١ الفئة    — تحدد عالم المقارنة كاملاً. مبنى يُقارَن بمبنى وأرض بأرض،
 *                ووسيط متر المبنى ≈ 4,950 مقابل ≈ 1,562 للأرض. الخطأ هنا
 *                يفسد كل ما بعده ⇒ أولاً.
 *   ٢ الحي     — بلا حي لا توجد صفقات مماثلة أصلاً. لا مخرَج إطلاقاً.
 *   ٣ المساحة  — النطاق السعري سعرُ متر؛ المساحة تحوّله إلى مبلغ. لا رقم بدونها.
 *   ٤ السعر    — لا يدخل FV، لكن بدونه لا AFG ولا OP — أي نطاق بلا حكم.
 *   ٥ الموقع   — يرفع النطاق من DISTRICT_LEVEL إلى PRECISE. يحسّن ولا يمنع ⇒ أخيراً واختياري.
 *
 * «الاستخدام» حُذف من الأسئلة: لا محرك ولا مزوّد يقرأ `landUse` اليوم، فسؤال
 * لا يغيّر المخرَج يوهم بدقة غير موجودة. الحقل باقٍ في العقد لحين استعماله.
 *
 * لماذا تتابع لا نموذج كامل: النموذج الكامل يطلب كل شيء دفعة واحدة فيبدو
 * استمارة؛ التتابع يجعل كل خطوة قراراً واحداً واضحاً، ويسمح بالتحقق عند
 * كل خطوة بدل تكديس الأخطاء في النهاية.
 *
 * الاتجاه البصري (لغة المخطط المساحي):
 *   · شبكة خطوط شعرية خلف السؤال.
 *   · مؤشر التقدّم خطوط شعرية لا نقاط ملوّنة — قياس لا زينة.
 *   · الأرقام بخط أحادي المسافة.
 *   · التركوازي مرة واحدة في هذه الشاشة: زر الفعل.
 *
 * مسار سريع: المكتب الذي يقيّم عشر أراضٍ لا يحتمل عشرة تتابعات، فله حقل
 * واحد يقبل «النرجس 500 2,400,000».
 *
 * لا منطق تقييم هنا — إدخال وتحقق شكلي فقط.
 */

import { useMemo, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { color } from '@/design/tokens';
import { Button, NumericField } from '@/components/primitives/Primitives';
import { useDistrictSearch, usePropertyCategories } from '@/services/market';
import { useValuationSession } from '@/app/AppShell';
import { JOURNEY_EXAMPLE, useJourney } from '@/components/guide/journey';
import { JourneyHint } from '@/components/guide/JourneyHint';
import {
  askingPerSqmHint,
  isValid,
  parseNumeric,
  parseLocationInput,
  parseQuickEntry,
  validateEvaluateForm,
  LOCATION_ERROR_MESSAGE,
  QUICK_ENTRY_ERROR_MESSAGE,
  type EvaluateFormErrors,
} from '@/services/validation';
import { num, perSqm } from '@/lib/format';
import { PROPERTY_CATEGORY_LABEL, type PropertyCategory } from '@/domain/types';

/**
 * `unavailableNote` يُعرض حين لا صفقات لهذه الفئة في البيانات.
 * الإتاحة تأتي من المزوّد لا من هنا: حين تصل صفقات المباني يتفعّل الخيار
 * وحده، ولا يمشي الزائر خمس خطوات إلى «لا توجد بيانات كافية» معروفة سلفاً.
 */
const CATEGORY_OPTIONS: {
  value: PropertyCategory;
  label: string;
  note: string;
  unavailableNote: string;
}[] = [
  {
    value: 'LAND',
    label: 'أرض',
    note: 'قطعة أرض سكنية أو فضاء',
    unavailableNote: 'غير متاح بعد — لا صفقات أراضٍ في البيانات الحالية',
  },
  {
    value: 'BUILDING',
    label: 'مبنى سكني',
    note: 'عمارة أو مجمع سكني',
    unavailableNote: 'غير متاح بعد — لا صفقات مبانٍ في البيانات الحالية',
  },
];

const STEPS = [
  {
    key: 'category',
    question: 'أرض أم مبنى؟',
    note: 'الفئة تحدد الصفقات التي نقارن بها. لا نخلط الفئتين أبداً.',
  },
  {
    key: 'district',
    question: 'أي حي؟',
    note: 'نقارن العقار بصفقات الفئة نفسها في الحي نفسه، المسجّلة رسمياً.',
  },
  {
    key: 'area',
    question: 'كم المساحة؟',
    note: 'بالمتر المربع كما في الصك أو الإعلان.',
  },
  {
    key: 'price',
    question: 'كم السعر المطلوب؟',
    note: 'السعر المطلوب لا يدخل في حساب النطاق السعري — نقارنه به فقط.',
  },
  {
    key: 'location',
    question: 'موقع العقار؟',
    note: 'اختياري. بدونه يبقى التقدير على مستوى الحي.',
  },
] as const;

const LAST_STEP = STEPS.length - 1;

/** مؤشر التقدّم — خطوط شعرية بعرض متساوٍ، بلا لون إشارة. */
function Progress({ current }: { current: number }): JSX.Element {
  return (
    <div
      data-testid="step-progress"
      aria-hidden="true"
      style={{ display: 'flex', gap: 5, marginTop: 16 }}
    >
      {STEPS.map((s, i) => (
        <div
          key={s.key}
          style={{
            height: 2,
            flex: 1,
            background:
              i < current ? 'rgba(217,222,227,.50)' : i === current ? color.text : color.hairline,
          }}
        />
      ))}
    </div>
  );
}

export default function EvaluateRoute(): JSX.Element {
  const navigate = useNavigate();
  const valuation = useValuationSession();
  const journey = useJourney();

  const [mode, setMode] = useState<'steps' | 'quick'>('steps');
  const [chosenCategory, setCategory] = useState<PropertyCategory>('LAND');
  const [step, setStep] = useState(0);

  // الأرض خيار ثابت: هي مجال التقييم الأصلي، وغياب صفقاتها يُعلَن عند الحي.
  // المباني لا تُتاح إلا حين يثبت المزوّد صفقاتها — والتحميل ليس إثباتاً.
  const availability = usePropertyCategories();
  const isAvailable = (c: PropertyCategory): boolean =>
    c === 'LAND' || availability?.[c] === true;
  const category: PropertyCategory = isAvailable(chosenCategory) ? chosenCategory : 'LAND';
  const [showError, setShowError] = useState(false);

  const [district, setDistrict] = useState(valuation.lastRequest?.district ?? '');
  const [city, setCity] = useState(valuation.lastRequest?.city ?? 'الرياض');
  const [area, setArea] = useState(
    valuation.lastRequest ? String(valuation.lastRequest.areaSqm ?? '') : '',
  );
  const [price, setPrice] = useState(
    valuation.lastRequest ? String(valuation.lastRequest.askingPrice ?? '') : '',
  );
  const [location, setLocation] = useState('');
  const [quick, setQuick] = useState('');
  const [quickTouched, setQuickTouched] = useState(false);

  const suggestions = useDistrictSearch(district, 5);
  const exactMatch = useMemo(
    () => suggestions.some((s) => s.name === district.trim()),
    [suggestions, district],
  );

  const errors: EvaluateFormErrors = validateEvaluateForm({ district, city, area, price });
  const hint = askingPerSqmHint(area, price);

  // الموقع اختياري: مدخل غير مفهوم يمنع الإرسال، وغيابه لا يمنعه.
  const parsedLocation = location.trim() ? parseLocationInput(location) : null;
  const locationError =
    parsedLocation && !parsedLocation.ok ? LOCATION_ERROR_MESSAGE[parsedLocation.error] : null;

  const stepError: string | null =
    step === 0
      ? null // الفئة لها قيمة افتراضية دائماً — لا خطأ ممكن.
      : step === 1
        ? (errors.district ?? null)
        : step === 2
          ? (errors.area ?? null)
          : step === 3
            ? (errors.price ?? null)
            : locationError;

  const busy = valuation.status === 'loading';

  async function submit(rawLocation: string): Promise<void> {
    const parsed = rawLocation.trim() ? parseLocationInput(rawLocation) : null;
    if (!isValid(errors) || (parsed && !parsed.ok)) {
      setShowError(true);
      return;
    }
    await valuation.run({
      city,
      district: district.trim(),
      areaSqm: parseNumeric(area),
      askingPrice: parseNumeric(price),
      propertyCategory: category,
      latitude: parsed?.ok ? parsed.value.latitude : null,
      longitude: parsed?.ok ? parsed.value.longitude : null,
    });
    navigate('/result');
  }

  function next(): void {
    if (stepError) {
      setShowError(true);
      return;
    }
    setShowError(false);
    if (step === LAST_STEP) {
      void submit(location);
      return;
    }
    setStep(step + 1);
  }

  function back(): void {
    setShowError(false);
    setStep((s) => Math.max(0, s - 1));
  }

  function goTo(index: number): void {
    setShowError(false);
    setStep(index);
  }

  /**
   * تعبئة قيمة الخطوة الحالية من مثال الجولة.
   *
   * الجولة **لا تضغط «التالي»** بعد التعبئة: التقدّم يبقى بيد الزائر، وإلا صار
   * يتفرّج على شاشة تملأ نفسها بدل أن يجرّب منتجاً.
   */
  function fillFromJourney(): void {
    setShowError(false);
    if (step === 0) setCategory('LAND');
    if (step === 1) {
      setDistrict(JOURNEY_EXAMPLE.district);
      setCity(JOURNEY_EXAMPLE.city);
    }
    if (step === 2) setArea(JOURNEY_EXAMPLE.area);
    if (step === 3) setPrice(JOURNEY_EXAMPLE.price);
  }

  /** Enter ينتقل للخطوة التالية — من داخل الحقول فقط، لا من الأزرار. */
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key !== 'Enter') return;
    if ((e.target as HTMLElement).tagName !== 'INPUT') return;
    e.preventDefault();
    next();
  }

  // ------------------------------------------------- الإجابات السابقة مختصرة
  const answered: { index: number; text: string; numeric: boolean }[] = [];
  if (step > 0) {
    answered.push({ index: 0, text: PROPERTY_CATEGORY_LABEL[category], numeric: false });
  }
  if (step > 1 && district.trim()) {
    answered.push({ index: 1, text: district.trim(), numeric: false });
  }
  if (step > 2 && Number.isFinite(parseNumeric(area))) {
    answered.push({ index: 2, text: `${num(parseNumeric(area))} م²`, numeric: true });
  }
  if (step > 3 && Number.isFinite(parseNumeric(price))) {
    answered.push({ index: 3, text: `${num(parseNumeric(price))} ر.س`, numeric: true });
  }

  // ---------------------------------------------------------- الإدخال السريع
  const quickParsed = quick.trim() ? parseQuickEntry(quick) : null;
  const quickReadError =
    quickParsed && !quickParsed.ok ? QUICK_ENTRY_ERROR_MESSAGE[quickParsed.error] : null;
  const quickFieldErrors: EvaluateFormErrors = quickParsed?.ok
    ? validateEvaluateForm({
        district: quickParsed.value.district,
        city,
        area: quickParsed.value.area,
        price: quickParsed.value.price,
      })
    : {};
  const quickFieldError =
    quickFieldErrors.district ?? quickFieldErrors.area ?? quickFieldErrors.price ?? null;

  function runQuick(): void {
    setQuickTouched(true);
    if (!quickParsed || !quickParsed.ok) return;
    if (quickFieldError || locationError) return;

    const v = quickParsed.value;
    // نحدّث الحقول أيضاً كي يجد المستخدم إجاباته إن عاد للتتابع.
    setDistrict(v.district);
    setArea(v.area);
    setPrice(v.price);

    const parsed = location.trim() ? parseLocationInput(location) : null;
    void valuation
      .run({
        city,
        district: v.district,
        areaSqm: parseNumeric(v.area),
        askingPrice: parseNumeric(v.price),
        propertyCategory: category,
        latitude: parsed?.ok ? parsed.value.latitude : null,
        longitude: parsed?.ok ? parsed.value.longitude : null,
      })
      .then(() => navigate('/result'));
  }

  return (
    <div className="nq-enter">
      {/* ------------------------------------------------------------ الترويسة */}
      <section
        className="nq-grid nq-bleed"
        style={{
          paddingBlock: '34px 26px',
          borderBottom: `1px solid ${color.hairlineSoft}`,
        }}
      >
        <div style={{ maxWidth: 620, margin: '0 auto' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <span className="nq-scheme">NITAQ / EVALUATE</span>
            <Button
              variant="quiet"
              size="sm"
              onClick={() => {
                setMode(mode === 'steps' ? 'quick' : 'steps');
                setShowError(false);
                setQuickTouched(false);
              }}
            >
              {mode === 'steps' ? 'إدخال سريع' : 'العودة للتتابع'}
            </Button>
          </div>

          {mode === 'steps' ? (
            <>
              {answered.length > 0 ? (
                <div
                  data-testid="answers-summary"
                  style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}
                >
                  {answered.map((a) => (
                    <button
                      key={a.index}
                      type="button"
                      onClick={() => goTo(a.index)}
                      style={{
                        background: 'transparent',
                        border: `1px solid ${color.hairline}`,
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 11.5,
                        color: color.textMuted,
                        cursor: 'pointer',
                      }}
                    >
                      <span className={a.numeric ? 'nq-num' : undefined}>{a.text}</span>
                    </button>
                  ))}
                </div>
              ) : null}

              <Progress current={step} />
              {/* عدّاد الخطوة معلومة لا زخرفة — يبقى فوق AA. */}
              <p style={{ marginTop: 8, fontSize: 11.5, color: color.textSubtle }}>
                الخطوة <span className="nq-num">{step + 1}</span> من{' '}
                <span className="nq-num">{STEPS.length}</span>
              </p>

              <h1
                style={{
                  marginTop: 18,
                  fontSize: 30,
                  fontWeight: 500,
                  lineHeight: 1.4,
                  letterSpacing: '-.01em',
                }}
              >
                {STEPS[step]!.question}
              </h1>
              <p
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  lineHeight: 1.9,
                  color: color.textSubtle,
                  maxWidth: 460,
                }}
              >
                {STEPS[step]!.note}
              </p>

              {journey.active ? (
                <JourneyHint
                  step={step}
                  total={STEPS.length}
                  onFill={step < LAST_STEP ? fillFromJourney : undefined}
                  onExit={journey.end}
                />
              ) : null}
            </>
          ) : (
            <>
              <h1
                style={{
                  marginTop: 18,
                  fontSize: 30,
                  fontWeight: 500,
                  lineHeight: 1.4,
                  letterSpacing: '-.01em',
                }}
              >
                إدخال سريع
              </h1>
              <p
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  lineHeight: 1.9,
                  color: color.textSubtle,
                  maxWidth: 460,
                }}
              >
                سطر واحد بالترتيب: الحي ثم المساحة ثم السعر.
              </p>
            </>
          )}
        </div>
      </section>

      {/* -------------------------------------------------------------- المحتوى */}
      <div style={{ maxWidth: 620, margin: '0 auto', paddingTop: 26 }}>
        {mode === 'steps' ? (
          /* key={step} يعيد تشغيل حركة الدخول عند كل سؤال — التغيّر محسوس. */
          <div key={step} className="nq-step" onKeyDown={onKeyDown}>
            {/* ------------------------------------------------- ١ · الفئة */}
            {step === 0 ? (
              <div role="group" aria-label="فئة العقار" style={{ display: 'grid', gap: 10 }}>
                {CATEGORY_OPTIONS.map((o) => {
                  const selected = category === o.value;
                  const available = isAvailable(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={selected}
                      disabled={!available}
                      onClick={() => setCategory(o.value)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 4,
                        padding: '16px 18px',
                        borderRadius: 12,
                        cursor: available ? 'pointer' : 'not-allowed',
                        textAlign: 'start',
                        background: selected ? 'rgba(0,229,199,.06)' : 'transparent',
                        border: `1px solid ${selected ? color.tealBorder : color.hairline}`,
                        color: available ? color.text : color.textSubtle,
                      }}
                    >
                      <span style={{ fontSize: 15, fontWeight: 600 }}>{o.label}</span>
                      <span style={{ fontSize: 12, color: color.textSubtle }}>
                        {available ? o.note : o.unavailableNote}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}

            {/* --------------------------------------------------- ٢ · الحي */}
            {step === 1 ? (
              <div>
                <label
                  htmlFor="district"
                  className="nq-label"
                  style={{ display: 'block', marginBottom: 8 }}
                >
                  الحي
                </label>
                <input
                  id="district"
                  className="nq-input"
                  value={district}
                  placeholder="اكتب اسم الحي..."
                  onChange={(e) => setDistrict(e.target.value)}
                  aria-invalid={Boolean(showError && errors.district)}
                />
                {district.trim() && !exactMatch && suggestions.length > 0 ? (
                  <ul
                    style={{
                      listStyle: 'none',
                      margin: '8px 0 0',
                      padding: 6,
                      background: color.surface700,
                      border: `1px solid ${color.hairlineSoft}`,
                      borderRadius: 12,
                      display: 'grid',
                      gap: 2,
                    }}
                  >
                    {suggestions.map((s) => (
                      <li key={s.districtId}>
                        <button
                          type="button"
                          onClick={() => {
                            setDistrict(s.name);
                            setCity(s.city);
                          }}
                          style={{
                            width: '100%',
                            textAlign: 'start',
                            padding: '9px 11px',
                            background: 'transparent',
                            border: 0,
                            borderRadius: 8,
                            cursor: 'pointer',
                            color: color.textBody,
                            fontSize: 13,
                          }}
                        >
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 10,
                            }}
                          >
                            <span>
                              {s.name} · {s.city}
                            </span>
                            {/* حالة التغطية معلنة قبل الاختيار — لا مفاجأة بعد الضغط. */}
                            {s.valuationAvailable === false ? (
                              <span
                                data-testid="district-coverage-hint"
                                style={{
                                  fontSize: 10.5,
                                  color: color.micro,
                                  border: `1px solid ${color.hairline}`,
                                  borderRadius: 999,
                                  padding: '2px 8px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                بيانات محدودة
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}

            {/* ----------------------------------------------- ٣ · المساحة */}
            {step === 2 ? (
              <div style={{ display: 'flex' }}>
                <NumericField
                  id="area"
                  label="المساحة"
                  unit="م²"
                  value={area}
                  onChange={setArea}
                  placeholder="750"
                  error={showError ? (errors.area ?? null) : null}
                />
              </div>
            ) : null}

            {/* ------------------------------------------------- ٤ · السعر */}
            {step === 3 ? (
              <div>
                <div style={{ display: 'flex' }}>
                  <NumericField
                    id="price"
                    label="السعر المطلوب"
                    unit="ر.س"
                    value={price}
                    onChange={setPrice}
                    placeholder="2,400,000"
                    error={showError ? (errors.price ?? null) : null}
                  />
                </div>
                {hint !== null ? (
                  <p
                    data-testid="asking-hint"
                    style={{ marginTop: 12, fontSize: 12.5, color: color.textSubtle }}
                  >
                    سعر المتر المطلوب{' '}
                    <span className="nq-num" dir="ltr">
                      {perSqm(hint)}
                    </span>
                  </p>
                ) : null}
              </div>
            ) : null}

            {/* ------------------------------------------------ ٥ · الموقع */}
            {step === 4 ? (
              <div>
                <label
                  htmlFor="location"
                  className="nq-label"
                  style={{ display: 'block', marginBottom: 8 }}
                >
                  الموقع <span style={{ color: color.micro, fontWeight: 400 }}>— اختياري</span>
                </label>
                <input
                  id="location"
                  className="nq-input"
                  dir="ltr"
                  style={{ textAlign: 'start' }}
                  value={location}
                  placeholder="24.6703, 46.5375 أو رابط خرائط جوجل"
                  onChange={(e) => setLocation(e.target.value)}
                  aria-invalid={Boolean(locationError)}
                  aria-describedby="location-help"
                />
                <p
                  id="location-help"
                  style={{ marginTop: 7, fontSize: 12, color: color.textSubtle, lineHeight: 1.7 }}
                >
                  {parsedLocation?.ok
                    ? 'سنستخدم هذا الموقع لاختيار الصفقات الأقرب — دقة أعلى.'
                    : 'بدون موقع سيكون التقدير على مستوى الحي. إضافته ترفع الدقة.'}
                </p>
              </div>
            ) : null}

            {/* الخطأ يظهر بعد المحاولة لا أثناء الكتابة.
                خطوتا المساحة والسعر تعرضان خطأهما داخل الحقل نفسه — فلا يُكرَّر هنا. */}
            {showError && stepError && step !== 2 && step !== 3 ? (
              <p role="alert" style={{ marginTop: 10, fontSize: 12, color: color.red }}>
                {stepError}
              </p>
            ) : null}

            {/* -------------------------------------------------------- الأزرار */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginTop: 26,
                flexWrap: 'wrap',
              }}
            >
              <Button onClick={next} disabled={busy}>
                {step === LAST_STEP ? (busy ? 'جارٍ الحساب...' : 'اعرض النطاق السعري') : 'التالي'}
              </Button>

              {step === LAST_STEP ? (
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    setLocation('');
                    void submit('');
                  }}
                >
                  تخطَّ
                </Button>
              ) : null}

              {step > 0 ? (
                <Button variant="ghost" onClick={back} disabled={busy}>
                  رجوع
                </Button>
              ) : null}
            </div>
          </div>
        ) : (
          /* ---------------------------------------------------- الإدخال السريع */
          <div
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
                e.preventDefault();
                runQuick();
              }
            }}
          >
            <label htmlFor="quick" className="nq-label" style={{ display: 'block', marginBottom: 8 }}>
              الحي والمساحة والسعر
            </label>
            <input
              id="quick"
              className="nq-input"
              value={quick}
              placeholder="النرجس 500 2,400,000"
              onChange={(e) => setQuick(e.target.value)}
              aria-invalid={Boolean(quickTouched && (quickReadError || quickFieldError))}
              aria-describedby="quick-read"
            />

            {/* ما فُهم يُعرض قبل الحساب — لا تخمين صامت. */}
            <p
              id="quick-read"
              data-testid="quick-read"
              style={{ marginTop: 10, fontSize: 12.5, color: color.textSubtle, lineHeight: 1.9 }}
            >
              {quickParsed?.ok ? (
                <>
                  الحي <span style={{ color: color.textBody }}>{quickParsed.value.district}</span> ·
                  المساحة{' '}
                  <span className="nq-num" dir="ltr" style={{ color: color.textBody }}>
                    {quickParsed.value.area}
                  </span>{' '}
                  م² · السعر{' '}
                  <span className="nq-num" dir="ltr" style={{ color: color.textBody }}>
                    {quickParsed.value.price}
                  </span>{' '}
                  ر.س
                </>
              ) : (
                'الترتيب: الحي ثم المساحة ثم السعر.'
              )}
            </p>

            {quickTouched && (quickReadError || quickFieldError) ? (
              <p role="alert" style={{ marginTop: 6, fontSize: 12, color: color.red }}>
                {quickReadError ?? quickFieldError}
              </p>
            ) : null}

            <div style={{ marginTop: 20 }}>
              <label
                htmlFor="quick-location"
                className="nq-label"
                style={{ display: 'block', marginBottom: 8 }}
              >
                الموقع <span style={{ color: color.micro, fontWeight: 400 }}>— اختياري</span>
              </label>
              <input
                id="quick-location"
                className="nq-input"
                dir="ltr"
                style={{ textAlign: 'start' }}
                value={location}
                placeholder="24.6703, 46.5375 أو رابط خرائط جوجل"
                onChange={(e) => setLocation(e.target.value)}
                aria-invalid={Boolean(locationError)}
              />
              {locationError ? (
                <p role="alert" style={{ marginTop: 6, fontSize: 12, color: color.red }}>
                  {locationError}
                </p>
              ) : null}
            </div>

            <div style={{ marginTop: 26 }}>
              <Button onClick={runQuick} disabled={busy}>
                {busy ? 'جارٍ الحساب...' : 'اعرض النطاق السعري'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
